import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { initPostHog } from "./lib/analytics";
import { ErrorBoundary } from "./components/ErrorBoundary";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// ── Sentry ─────────────────────────────────────────────────────────────────────
if (SENTRY_DSN && SENTRY_DSN !== "your_sentry_dsn_here") {
  Sentry.init({
    dsn: SENTRY_DSN,
    release: `field24-web@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // ── Privacy ───────────────────────────────────────────────────────────
        // Don't mask ALL text — that makes the replay a sea of asterisks and
        // defeats the purpose of seeing "where the tech's app froze".
        // Instead, only mask explicitly sensitive fields by CSS selector.
        maskAllText: false,
        blockAllMedia: false,

        // Mask specific sensitive inputs (passwords, card numbers, OTPs)
        mask: ['input[type="password"]', '[data-sentry-mask]', '.sensitive'],
        // Block profile photos / camera previews
        block: ['.sentry-block', '[data-sentry-block]'],

        // ── Network capture ───────────────────────────────────────────────────
        // Record which API calls were in-flight when the app froze.
        // Especially useful in South Africa where high latency means a single
        // timed-out POST /checkin can freeze the entire job workflow.
        networkDetailAllowUrls: [window.location.origin, '/api/'],
        networkCaptureBodies: false, // don't log request/response bodies (PII)
        networkRequestHeaders: ['Content-Type', 'X-Request-Id'],
        networkResponseHeaders: ['Content-Type'],
      }),
    ],

    // Performance tracing — 20 % in prod, 100 % in dev
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

    // ── Replay sample rates ───────────────────────────────────────────────────
    // Record 30 % of all sessions so you get enough data to spot patterns
    // (high-latency page loads, stuck loaders, blank screens).
    replaysSessionSampleRate: import.meta.env.PROD ? 0.3 : 1.0,
    // Always record the last N seconds leading up to any crash / error.
    replaysOnErrorSampleRate: 1.0,
  });
} else {
  console.log("[sentry] VITE_SENTRY_DSN not configured — skipping Sentry init.");
}

// ── PostHog ────────────────────────────────────────────────────────────────────
initPostHog();

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>,
);
